import { useState } from 'react';
import Button from '../../components/Button';
import { customToast } from '../../components/ToastAlert/customToast';
import {
  useAutomationWebhookConfigQuery,
  useUpdateAutomationWebhookConfigMutation,
  useTestAutomationWebhookMutation,
} from '../../../generated/graphql';
import { TRIGGER_OPTIONS } from './automationMetadata';
import { logger } from '../../../lib/logger';

function examplePayloadForEvent(eventType: string): string {
  const base = {
    provider_id: 'provider-uuid',
    program_id: 'program-uuid',
    patient_id: 'patient-uuid',
  };
  const payloads: Record<string, object> = {
    assessment_completed: { ...base, assessment_type: 'PHQ9', score: 8 },
    assessment_submitted: { ...base, assessment_type: 'GAD7' },
    appointment_scheduled: { ...base, appointment_id: 'apt-uuid' },
    appointment_missed: { ...base, appointment_id: 'apt-uuid' },
    session_completed: { ...base, session_id: 'session-uuid' },
    client_tag_added: {
      ...base,
      tag_id: 'tag-uuid',
      tag_name: 'VIP',
      tag_color: '#FF5733',
      action: 'added',
    },
    client_tag_removed: {
      ...base,
      tag_id: 'tag-uuid',
      tag_name: 'VIP',
      tag_color: '#FF5733',
      action: 'removed',
    },
    client_created: {
      ...base,
      email: 'client@example.com',
      first_name: 'Jane',
      last_name: 'Doe',
    },
    invoice_paid: { ...base, amount: 150, currency: 'usd' },
    practitioner_assigned: { ...base, assigned_provider_user_id: 'user-uuid' },
    service_booked: { ...base, calendly_event_type_name: 'Consultation' },
    program_start_date: { ...base, offset: -3, trigger_time: '09:00' },
    program_end_date: { ...base, offset: 5, trigger_time: '14:00' },
  };
  const data = payloads[eventType] ?? base;
  return JSON.stringify(data, null, 2);
}

export default function ConfigurationTab() {
  const [urlInput, setUrlInput] = useState('');
  const [selectedEventType, setSelectedEventType] = useState(
    () => TRIGGER_OPTIONS[0]?.value ?? 'assessment_completed',
  );
  const { data, loading, error, refetch } = useAutomationWebhookConfigQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [updateConfig, { loading: saving }] =
    useUpdateAutomationWebhookConfigMutation();
  const [testWebhook, { loading: testing }] =
    useTestAutomationWebhookMutation();

  const webhookUrl = data?.automationWebhookConfig?.webhookUrl ?? '';
  /**
   * The URL displayed in the input field.
   * - Shows the user's input while typing (urlInput)
   * - Shows the saved server URL when input is empty (webhookUrl)
   * - After save, urlInput is cleared, reverting to server value
   */
  const isUrlControlled = urlInput !== '';
  const displayUrl = isUrlControlled ? urlInput : webhookUrl;

  const handleSave = async () => {
    const value = (displayUrl || '').trim() || null;
    try {
      await updateConfig({
        variables: {
          input: { webhookUrl: value },
        },
      });
      setUrlInput('');
      refetch();
      customToast.success('Webhook URL saved.');
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to save webhook URL.';
      logger.error('Failed to save webhook URL', { error: e });
      customToast.error(message);
    }
  };

  const handleTest = async () => {
    const payload = examplePayloadForEvent(selectedEventType);
    try {
      const result = await testWebhook({
        variables: {
          input: { eventType: `automation.${selectedEventType}`, payload },
        },
      });
      const res = result.data?.testAutomationWebhook;
      if (res?.success) {
        customToast.success(
          `Test sent (${res.statusCode ?? 200}). ${res.message ?? ''}`.trim(),
        );
      } else {
        customToast.error(res?.message || 'Test request failed.');
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to send test.';
      logger.error('Failed to send test webhook', { error: e });
      customToast.error(message);
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-md border border-neutral-100 bg-white p-6">
        <p className="text-sm text-neutral-120">Loading configuration…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-100 bg-red-50 p-6">
        <p className="text-sm text-red-800">
          Failed to load webhook configuration. Please refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-md border border-neutral-100 bg-white p-6">
        <h3 className="text-sm font-sans font-semibold text-neutral-150">
          Webhook URL
        </h3>
        <p className="text-xs text-neutral-120 mt-1 mb-3">
          When set, all automation events are sent to this URL as POST requests
          with a JSON body (eventType, eventId, data).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <input
              type="url"
              value={displayUrl}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://your-server.com/webhooks/automation"
              className="text-sm w-full rounded-md border border-neutral-75 bg-neutral-50 px-3 py-2 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
            />
          </div>
          <Button
            title={saving ? 'Saving…' : 'Save'}
            theme="primary"
            size="small"
            disabled={saving}
            onClick={handleSave}
          />
        </div>
      </div>

      <div className="rounded-md border border-neutral-100 bg-white p-6">
        <h3 className="text-sm font-sans font-semibold text-neutral-150">
          Example payload and test
        </h3>
        <p className="text-xs text-neutral-120 mt-1 mb-4">
          Pick an event type to see the example payload, then send a test to
          your endpoint.
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-neutral-130 mb-1 block font-semibold">
                Event type
              </label>
              <select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="text-sm min-w-[220px] rounded-md border border-neutral-75 bg-neutral-50 py-2 pl-3 pr-10 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
              >
                {TRIGGER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              title="Send test payload"
              theme="secondary"
              size="small"
              disabled={testing || !webhookUrl}
              onClick={handleTest}
            />
          </div>
          <pre className="text-neutral-130 overflow-x-auto rounded border border-neutral-75 bg-neutral-50 p-3 text-[11px]">
            {examplePayloadForEvent(selectedEventType)}
          </pre>
        </div>
      </div>
    </div>
  );
}
